// Builder intent only. Calculated dates and Fast Lane eligibility are not intent.
const flag = value => value === true || value === 1 || value === '1'
export function timingIntent (value = {}) {
  value = value || {}
  return { event_order: flag(value.event_order), in_hands_date: typeof value.in_hands_date === 'string' ? value.in_hands_date.slice(0, 64) : '',
    future_ship_date_boolean: flag(value.future_ship_date_boolean), future_ship_date: typeof value.future_ship_date === 'string' ? value.future_ship_date.slice(0, 64) : '', fast_lane: flag(value.event_order) && flag(value.fast_lane) }
}
export function validTimingDate (value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false
  const date = new Date(value + 'T00:00:00Z')
  return !isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
}
// Preserve V1 semantics: date-only input parses at UTC midnight while the local
// current instant is advanced by 90 calendar dates without clearing its time.
export function prebookEligible (value, now = new Date()) {
  const timing = timingIntent(value)
  if (!timing.future_ship_date_boolean || !timing.future_ship_date) return false
  const selected = new Date(timing.future_ship_date)
  if (isNaN(selected.getTime())) return false
  const threshold = new Date(now)
  threshold.setDate(threshold.getDate() + 90)
  return selected >= threshold
}
export function timingErrors (value) {
  const t = timingIntent(value); const errors = []
  if (t.event_order && !validTimingDate(t.in_hands_date)) errors.push('Enter a valid Full Order Must Arrive By date (YYYY-MM-DD).')
  if (t.future_ship_date_boolean && !validTimingDate(t.future_ship_date)) errors.push('Enter a valid Do Not Ship Before date (YYYY-MM-DD).')
  if (!errors.length && t.event_order && t.future_ship_date_boolean && t.future_ship_date > t.in_hands_date) errors.push('Do Not Ship Before cannot be later than Full Order Must Arrive By.')
  return errors
}
export function timingSummary (value, confirmed = false) {
  const t = timingIntent(value); const rows = []
  if (t.event_order) rows.push('Full Order Must Arrive By: ' + (t.in_hands_date || 'Date required'))
  if (t.future_ship_date_boolean) rows.push('Do Not Ship Before: ' + (t.future_ship_date || 'Date required'))
  if (t.fast_lane) rows.push(confirmed ? 'Fast Lane confirmed for the current configuration' : 'Fast Lane requested — pending eligibility confirmation; not applied')
  return rows.length ? rows : ['Standard scheduling']
}
export function nativeTiming (value, confirmed = false) {
  const t = timingIntent(value)
  return { event_order: t.event_order, in_hands_date: t.event_order ? t.in_hands_date : '', future_ship_date_boolean: t.future_ship_date_boolean,
    future_ship_date: t.future_ship_date_boolean ? t.future_ship_date : '', fast_lane: t.fast_lane && confirmed ? 1 : 0 }
}
export function sameTimingWorkspace (a, b) {
  if (!a || !b) return false
  if (a.timing_workspace_id && b.timing_workspace_id) return a.timing_workspace_id === b.timing_workspace_id
  const ids = v => (v.sections || []).map(s => s.id).filter(Boolean).sort().join('|')
  return !!ids(a) && ids(a) === ids(b) && String(a.saved_id || '') === String(b.saved_id || '') && String((a.customer_info || {}).customer_number || '') === String((b.customer_info || {}).customer_number || '')
}
export function initializeTiming (builder, reviewEnvelope) {
  if (builder.scheduling_intent) { builder.scheduling_intent = timingIntent(builder.scheduling_intent); return }
  const review = reviewEnvelope && reviewEnvelope.draft
  const matches = review && sameTimingWorkspace(builder, review.builder)
  builder.scheduling_intent = timingIntent(matches ? (review.builder.scheduling_intent || review.order) : {})
  builder.scheduling_intent_updated_at = matches ? (reviewEnvelope.updated_at || '') : ''
}
export function mergeTimingOwnership (base, current, result) {
  if (!base.scheduling_intent) {
    if ((base.sections || []).length && current.scheduling_intent && !sameTimingWorkspace(base, current)) {
      delete result.scheduling_intent
      delete result.scheduling_intent_updated_at
      delete result.timing_workspace_id
    }
    return result
  }
  const newer = sameTimingWorkspace(base, current) && current.scheduling_intent && Date.parse(current.scheduling_intent_updated_at || '') > Date.parse(base.scheduling_intent_updated_at || '')
  if (!newer) {
    result.scheduling_intent = timingIntent(base.scheduling_intent)
    result.scheduling_intent_updated_at = base.scheduling_intent_updated_at || ''
    result.timing_workspace_id = base.timing_workspace_id
  }
  return result
}
export const canRequestFastLane = (user, enabled) => !!enabled && flag(user && user.allow_fast_lane)
export function fastLaneBusinessDaysUntil (target, now = new Date(), holidays = []) {
  if (!validTimingDate(target)) return null
  const start = new Date(now)
  const hour = parseInt(now.toLocaleString('en-US', { timeZone: 'America/Chicago', hour12: false, hour: 'numeric' }), 10)
  if (hour >= 14) start.setDate(start.getDate() + 1)
  start.setHours(0, 0, 0, 0)
  const end = new Date(target + 'T00:00:00'); end.setHours(0, 0, 0, 0)
  if (end < start) return 0
  let count = 0
  for (const cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
    const date = [cursor.getFullYear(), String(cursor.getMonth() + 1).padStart(2, '0'), String(cursor.getDate()).padStart(2, '0')].join('-')
    if (![0, 6].includes(cursor.getDay()) && !holidays.includes(date)) count++
  }
  return count
}
export function fastLanePromptFingerprint (intent, enabled, userAllowed, now = new Date(), holidays = [], workspace = '') {
  const timing = timingIntent(intent)
  const days = fastLaneBusinessDaysUntil(timing.in_hands_date, now, holidays)
  return JSON.stringify([!!enabled, !!userAllowed, timing.event_order, timing.in_hands_date, days, workspace || '', now.toLocaleDateString('en-CA', { timeZone: 'America/Chicago' }), parseInt(now.toLocaleString('en-US', { timeZone: 'America/Chicago', hour12: false, hour: 'numeric' }), 10) >= 14])
}
export function requiresFastLanePrompt (intent, enabled, userAllowed, now = new Date(), holidays = []) {
  const timing = timingIntent(intent)
  if (!enabled || !userAllowed || !timing.event_order || !timing.in_hands_date) return false
  const days = fastLaneBusinessDaysUntil(timing.in_hands_date, now, holidays)
  return days !== null && days >= 4 && days <= 8
}
export function fastLaneConfigurationCandidates (candidates, active) {
  const rows = Array.isArray(candidates) ? candidates : []
  if (!active) return { eligible: rows, unavailable: [] }
  return {
    eligible: rows.filter(row => Number(row && row.technique && row.technique.allow_fastlane) === 1),
    unavailable: rows.filter(row => Number(row && row.technique && row.technique.allow_fastlane) !== 1).map(row => Object.assign({}, row, { technique: Object.assign({}, row.technique), fast_lane_unavailable: true, label: (row.label || '') + ' · Not available for Fast Lane' }))
  }
}

// Fingerprint only date-authority inputs; quote rates and calculated dates can change independently.
export function shipDateInputKey (payload, intent) {
  const colors = rows => (rows || []).map(c => [c.color_id, (c.sizes || []).map(s => [s.size_id, s.quantity]), colors(c.inner_colors)])
  return JSON.stringify([payload.future_ship_date_boolean, payload.future_ship_date, payload.fast_lane,
    intent && nativeTiming(intent).event_order, intent && nativeTiming(intent).in_hands_date,
    (payload.groups || []).map(g => [g.group_id, (g.order_items || []).map(i => [
      i.program_info && i.program_info.program_id, i.style_info && i.style_info.style_id, !!i.blank_order,
      (i.order_item_locations || []).map(l => l.technique && l.technique.technique_id), colors(i.order_item_colors)
    ])])])
}
